// Kilat 4U washer builder: the <k4u-build> element on /pages/build. Loaded only by sections/k4u-build.liquid.
// The page is a short list of steps, all open at once like Apple's Mac configurator: the washer (on /pages/build), then one step per
// part the shop can add something to (each says what is already in the box), then the wash liquids. Pure logic (prices, the 3-Step Set rule, URL codec, WhatsApp text)
// is in k4u-build-core.js; motion is in k4u-build-fx.js (loaded on intent, skipped for reduced motion / Save-Data / 2g-3g).
import { CartLinesUpdateEvent } from '@shopify/events';

const calmQuery = matchMedia('(prefers-reduced-motion: reduce)');
const slowNet = () => {
  const c = navigator.connection;
  return !!c && (c.saveData || ['slow-2g', '2g', '3g'].includes(c.effectiveType));
};
const calm = () => calmQuery.matches || slowNet();
const mem = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* private window */ } },
  sget: (k) => { try { return sessionStorage.getItem(k); } catch { return null; } },
  sset: (k, v) => { try { sessionStorage.setItem(k, v); } catch { /* private window */ } },
};
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
const PLACEHOLDER = 9e12; // placeholder part ids are 9e12 + n (a few of them): ordered on WhatsApp until a real product is picked
const waOnlyMode = () => document.documentElement.classList.contains('k4u-wa-only');
const isPlaceholder = (id) => id > PLACEHOLDER && id < PLACEHOLDER + 1e6; // real Shopify variant ids are ~5e13, far above

// i18n:begin
// The words on this page. The section renders the visitor's language into the data-k4u-build-data island (cat.t: key -> text, or
// {one, other}; cat.lang). EN is today's English, the same text as locales/en.default.json: it answers when there is no island or a key
// is missing, so a shopper never sees a key name. Every value that goes into innerHTML below passes through esc().
const EN = {
  'build.link_dropped': "One item in this link isn't available, so we left it out.",
  'build.resume': 'Pick up where you left off: {{ summary }}, {{ total }}',
  'build.hint': 'Tap a part to see extras',
  'build.plate_label': '{{ label }}. Tap to see the extras.',
  'build.step_liquids': 'Wash liquids',
  'build.status_upgraded': 'Extra added',
  'build.status_in_box': 'In the box',
  'build.status_optional': 'Optional',
  'build.rack_all': 'All three',
  'build.rack_count': '{{ count }} of 3',
  'actions.choose': 'Choose',
  'build.box_summary': 'Also in the box',
  'build.receipt': 'Your build',
  'actions.add_to_cart': 'Add to cart',
  'build.wa_send': 'Send my build on WhatsApp',
  'build.step_washer': 'Your washer',
  'build.cta_change': 'Change',
  'build.all_three_set': 'All three: the 3-Step Set',
  'build.bottles_of_three': '{{ count }} of 3 bottles',
  'build.none_chosen': 'None chosen yet',
  'build.sub_upgraded': 'Extra: {{ name }}',
  'build.sub_in_box': 'In the box: {{ name }}',
  'build.cta_upgrade': 'Add extra',
  'build.cta_add': 'Add?',
  'build.washer_group': 'Washer',
  'build.spec_pressure': 'Pressure',
  'build.spec_motor': 'Motor',
  'build.spec_hose': 'Hose',
  'build.spec_different': 'Different',
  'build.liquids_ask': 'Pick the bottles you want. They are used in this order.',
  'build.added': 'Added',
  'build.kit_label': 'Your kit',
  'build.kit_remove': 'Remove {{ name }}',
  'build.head_washer': 'Choose your model.',
  'build.head_keep': 'Keep it, or add a better one.',
  'build.head_add': 'Add what you like.',
  'build.head_liquids': 'Pick your bottles.',
  'build.ask_upgrade': 'Your {{ machine }} already comes with a {{ part }}. Want a better one on top? You keep the one in the box.',
  'build.ask_add': 'Want to add {{ part }}?',
  'build.keep_stock': 'Just the one in the box',
  'build.included': 'Included',
  'build.no_thanks': 'No thanks',
  'build.skip_this': 'Skip this one',
  'build.fit_unknown': 'Ask us to confirm fit',
  'build.fit_yes': 'Fits the {{ machine }}',
  'build.order_wa': 'Order on WhatsApp',
  'content.product_badge_sold_out': 'Sold out',
  'build.keep_default': 'Your original stays in the box as a spare.',
  'build.comes_with': 'Comes with your washer',
  'build.save_vs_set': '{{ amount }} less than the set on its own',
  'build.add_it': 'Add it: {{ delta }}',
  'build.preorder_wa_only': 'Extras are ordered on WhatsApp for now.',
  'build.announce': '{{ summary }}. Total {{ total }}.',
  'build.receipt_lines': { one: 'Your build, {{ count }} line', other: 'Your build, {{ count }} lines' },
  'build.fit_to_confirm': '(fit to confirm)',
  'build.note_set': '3-Step Set: pre-wash, tyre and wheel, main wash. Comes with your washer.',
  'build.note_vs_singles': 'That is {{ amount }} less than three single bottles.',
  'build.note_spare': 'Extras are bought on top, priced in full. You still get the part that comes with your washer, in the box.',
  'build.compare_title': 'Not sure which one to get?',
  'build.compare_sub': '1–2 cars now and then: F4 is enough. 3–5 cars in one go: P6 Pro.',
  'build.note_wa_only': 'Your extras are ordered on WhatsApp for now, so the cart button is off. Tap WhatsApp and we confirm the price, fit and delivery.',
  'build.share': 'Share',
  'build.note_in_cart': 'You already have a washer in your cart. This adds another.',
  'build.note_delivery': 'Delivery is worked out at checkout. Washers ship free in Sarawak.',
  'build.confirm_wa': 'Confirmed on WhatsApp',
  'build.note_delivery_wa': 'We confirm delivery to your town on WhatsApp. Washers ship free in Sarawak.',
  'build.copy_link': 'Copy build link',
  'build.share_title': 'My Kilat 4U washer build',
  'build.copied': 'Copied',
  'build.copy_fail': 'Press and hold the address bar',
  'build.add_error': "Couldn't add your build. Try again, or send it on WhatsApp instead."
};
const fmt = (s, v) => String(s ?? '').replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (v && k in v ? v[k] : m));
const rule = (c, n) => { try { return new Intl.PluralRules((c && c.lang) || 'en').select(n); } catch { return 'other'; } };
const dec = (s) => s.replace(/&#39;|&#x27;/g, "'").replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&'); // Liquid's t filter HTML-escapes
const word = (c, k, n) => {
  for (const o of [c && c.t, EN]) {
    let x = o && o[k];
    if (x && typeof x === 'object') x = x[rule(c, n)] || x.other;
    if (typeof x === 'string' && x && !/^translation missing/i.test(x)) return o === EN ? x : dec(x);
  }
  return '';
};
const tx = (c, k, v) => fmt(word(c, k), v);
const pl = (c, k, n, v) => fmt(word(c, k, n), { count: n, ...v });
// i18n:end

class K4UBuild extends HTMLElement {
  async connectedCallback() {
    if (this._on) return;
    this._on = true;
    this.core = await import(this.dataset.core);
    this.cat = JSON.parse(this.querySelector('[data-k4u-build-data]').textContent);
    this.stage = this.querySelector('[data-stage]');
    this.floor = this.querySelector('[data-floor]');
    this.platesEl = this.querySelector('[data-plates]');
    this.seg = this.querySelector('[data-seg]');
    this.chip = this.querySelector('[data-chip]');
    this.panel = this.querySelector('[data-panel]');
    this.liveEl = this.querySelector('[data-live]');
    this.resume = this.querySelector('[data-resume]');
    this.icons = {};
    this.querySelectorAll('template[data-icons] [data-i]').forEach((n) => { this.icons[n.dataset.i] = n.innerHTML; });
    this.state = { machine: null, bottles: [], parts: [] };
    this.open = null;
    this.fx = null;
    this.writeUrl = debounce(() => this.pushUrl(), 250);
    this.announce = debounce((t) => { this.liveEl.textContent = t; }, 400);
    this.bind();
    if (this.dataset.fixed) this.restoreFixed(); else this.restore();
    this.dataset.ready = '';
  }

  // ---------- setup ----------
  loadFx() {
    if (this._fxp) return this._fxp;
    if (calm()) return (this._fxp = Promise.resolve(null));
    this._fxp = import(this.dataset.fx).then((m) => (this.fx = m), () => null);
    return this._fxp;
  }

  bind() {
    const warm = () => { this.loadFx(); };
    this.floor.addEventListener('pointerenter', warm, { once: true, passive: true });
    this.addEventListener('pointerdown', (e) => {
      warm();
      const el = e.target instanceof Element ? e.target.closest('[data-press]') : null;
      if (!el || calm()) return;
      const r = el.getBoundingClientRect();
      el.style.setProperty('--rx', `${e.clientX - r.left}px`);
      el.style.setProperty('--ry', `${e.clientY - r.top}px`);
      el.classList.remove('k4u-ripple');
      void el.offsetWidth;
      el.classList.add('k4u-ripple');
    }, { passive: true });
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 4000 }); else setTimeout(warm, 2500);
    this.addEventListener('click', (e) => this.onClick(e));
  }

  restore() {
    const hash = location.hash || location.search;
    const dec = this.core.decode(hash, this.cat);
    if (dec.state.machine) {
      this.apply(dec.state, { instant: true });
      if (dec.dropped.length) this.say(this.t('build.link_dropped'));
      return;
    }
    const last = mem.get('k4u-build:last');
    if (last) {
      const d = this.core.decode(last, this.cat);
      if (d.state.machine) {
        const r = this.core.resolve(d.state, this.cat);
        this.resume.textContent = this.t('build.resume', { summary: r.summary, total: this.core.money(r.total) });
        this.resume.hidden = false;
        this.resume.addEventListener('click', (e) => { e.preventDefault(); this.resume.hidden = true; this.apply(d.state, {}); });
      }
    }
  }

  // On a machine page the washer is the page's own: no landing, no URL, no 'pick up where you left off'.
  // The stage is only built when the section is about to be seen, so its pictures never compete with the page's first paint.
  restoreFixed() {
    const k = this.dataset.fixed;
    if (!this.machineData(k)) return;
    const go = () => this.apply({ machine: k, bottles: [], parts: [] }, { instant: true });
    if (!('IntersectionObserver' in window)) { go(); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { io.disconnect(); go(); } }, { rootMargin: '700px 0px' });
    io.observe(this);
    new IntersectionObserver(([e]) => document.documentElement.toggleAttribute('data-k4u-building', e.isIntersecting), { threshold: 0.05 }).observe(this);
  }

  // the address a build link points to: the builder page when the tool is embedded in a machine page
  linkBase() { return `${location.origin}${this.dataset.buildUrl && this.dataset.fixed ? this.dataset.buildUrl : location.pathname}`; }

  // ---------- state ----------
  t(k, v) { return tx(this.cat, k, v); }
  pl(k, n, v) { return pl(this.cat, k, n, v); }
  machineData(k) { return this.cat.machines.find((m) => m.k === k); }
  partOf(variantId) { return this.cat.parts.find((x) => x.v.some((v) => v.id === variantId)); }
  slotMeta(id) { return this.cat.slots.find((s) => s.id === id) || {}; }
  stockFor(k) { return this.cat.box.filter((b) => b.m === k || b.m === 'both'); }
  equippedIn(slot) { for (const id of this.state.parts) { const p = this.partOf(id); if (p && p.slot === slot) return { part: p, id }; } return null; }
  get r() { return this._r; }

  async apply(next, opts = {}) {
    const first = this.dataset.state !== 'composed';
    const prev = this.state;
    this.state = this.core.normalize(next, this.cat).state;
    if (!this.state.machine) return;
    if (first && this.resume) this.resume.hidden = true;
    if (first && this.state.bottles.length === 0 && this.cat.defaultSet && !opts.instant) this.state.bottles = ['1', '2', '3'];
    this._r = this.core.resolve(this.state, this.cat);
    if (first) await this.compose(opts);
    else if (prev.machine !== this.state.machine) await this.swapMachine(this.state.machine);
    this.render();
  }

  async pickMachine(k, from) {
    if (this.state.machine === k) return;
    await this.apply({ ...this.state, machine: k }, { from });
  }

  toggleBottle(k) {
    const has = this.state.bottles.includes(k);
    this.change({ ...this.state, bottles: has ? this.state.bottles.filter((b) => b !== k) : [...this.state.bottles, k] });
  }

  toggleAll() { this.change({ ...this.state, bottles: this.state.bottles.length === 3 ? [] : ['1', '2', '3'] }); }

  choosePart(variantId, fromEl) {
    const p = this.partOf(variantId);
    if (!p) return;
    const meta = this.slotMeta(p.slot);
    let parts = this.state.parts.filter((id) => id !== variantId);
    const had = parts.length !== this.state.parts.length;
    if (!had) {
      if (meta.mode === 'one') parts = parts.filter((id) => { const o = this.partOf(id); return !o || o.slot !== p.slot; });
      parts.push(variantId);
    }
    this.change({ ...this.state, parts }, { equipped: had ? null : variantId, from: fromEl, slot: p.slot });
  }

  keepStock(slot) {
    const parts = this.state.parts.filter((id) => { const p = this.partOf(id); return !p || p.slot !== slot; });
    this.change({ ...this.state, parts }, { slot });
  }

  change(next, info = {}) {
    this.state = this.core.normalize(next, this.cat).state;
    this.render(info);
  }

  // ---------- composing the stage ----------
  async compose(opts) {
    const k = this.state.machine;
    const m = this.machineData(k);
    this.machineEl = document.createElement('div');
    this.machineEl.className = 'k4u-build__machine';
    this.machineEl.tabIndex = 0;
    this.machineEl.setAttribute('role', 'img');
    this.machineEl.setAttribute('aria-label', `Lutian ${m.n}`);
    this.machineEl.innerHTML = `<div class="k4u-build__float"><img src="${esc(m.img)}" alt="" width="717" height="542" decoding="async"></div>`;
    this.shadowEl = document.createElement('i');
    this.shadowEl.className = 'k4u-build__shadow';
    this.hintEl = document.createElement('span');
    this.hintEl.className = 'k4u-build__hint';
    this.hintEl.textContent = this.t('build.hint');
    this.kitEl = document.createElement('div');
    this.kitEl.className = 'k4u-build__kit';
    this.kitEl.setAttribute('role', 'group');
    this.kitEl.setAttribute('aria-label', this.t('build.kit_label'));
    this.stage.append(this.shadowEl, this.machineEl, this.kitEl, this.hintEl);
    this.buildSeg();
    this.buildPanel();
    const otherTile = this.floor.querySelector(`[data-tile]:not([data-tile="${k}"]) img`);
    const other = otherTile ? { src: otherTile.currentSrc || otherTile.src, rect: otherTile.getBoundingClientRect() } : null;
    const tileImg = this.floor.querySelector(`[data-tile="${k}"] img`);
    const from = opts.from || (tileImg ? tileImg.getBoundingClientRect() : null);
    this.dataset.state = 'composed';
    this.panel.hidden = false;
    this.seg.hidden = false;
    this.platesRender();
    const playHero = !opts.instant && !mem.sget('k4u-build:hero');
    if (playHero) {
      mem.sset('k4u-build:hero', '1');
      await this.loadFx();
      if (this.fx) await this.fx.hero(this, { from, other }).catch(() => {});
    }
    if (!calm()) this.dataset.float = '';
    if (!opts.instant) {
      const y = this.stage.getBoundingClientRect().top;
      if (y < 0 || y > innerHeight * 0.4) this.stage.scrollIntoView({ block: 'start', behavior: calm() ? 'auto' : 'smooth' });
    }
    if (!waOnlyMode()) this.cartCheck();
  }

  async swapMachine(b) {
    await this.loadFx();
    const m = this.machineData(b);
    const img = this.machineEl.querySelector('img');
    const run = async () => {
      img.src = m.img;
      this.machineEl.setAttribute('aria-label', `Lutian ${m.n}`);
      this.platesRender();
      this.buildSteps();
    };
    if (this.fx) await this.fx.swap(this, run); else await run();
  }

  buildSeg() {
    this.seg.innerHTML = this.cat.machines.map((m) => `<button type="button" role="radio" aria-checked="false" data-act="machine" data-v="${esc(m.k)}" data-press>${esc(m.n)}</button>`).join('');
  }

  // ---------- stage plates: only the parts you can upgrade ----------
  upgradableSlots() {
    return this.core.slotsFor(this.cat, this.state.machine).filter((s) => s.state === 'tab');
  }

  platesRender() {
    const slots = this.upgradableSlots();
    const html = slots.map((s) => {
      const stock = s.stock.find((b) => b.stage !== false) || s.stock[0];
      return `<button type="button" class="k4u-build__plate${stock ? '' : ' is-empty'}" data-act="plate" data-slot="${esc(s.id)}" data-press aria-label="${esc(this.t('build.plate_label', { label: s.label }))}">
        <img src="${esc(stock ? stock.img : '')}" alt="" width="96" height="96" decoding="async"${stock ? '' : ' hidden'}>
        <em aria-hidden="true">+</em><span><b>${esc(s.label)}</b><small data-status></small></span></button>`;
    });
    html.push(`<div class="k4u-build__rack" data-rack aria-hidden="true">${(this.cat.bottles || []).map((b, i) => `<i class="k4u-build__band" style="--c:${esc(b.c)};--i:${i}" data-band="${esc(b.k)}"></i>`).join('')}<span><b>${esc(this.t('build.step_liquids'))}</b><small data-rack-status></small></span></div>`);
    this.platesEl.innerHTML = html.join('');
    this.plateState();
  }

  plateState() {
    const k = this.state.machine;
    const slots = this.core.slotsFor(this.cat, k);
    this.platesEl.querySelectorAll('.k4u-build__plate').forEach((pl) => {
      const s = slots.find((x) => x.id === pl.dataset.slot);
      const eq = this.equippedIn(pl.dataset.slot);
      const stock = s && (s.stock.find((b) => b.stage !== false) || s.stock[0]);
      const img = pl.querySelector('img');
      const src = eq ? eq.part.img : stock ? stock.img : '';
      if (src && img.getAttribute('src') !== src) img.src = src;
      img.hidden = !src;
      pl.classList.toggle('is-on', !!eq);
      pl.querySelector('[data-status]').textContent = this.t(eq ? 'build.status_upgraded' : stock ? 'build.status_in_box' : 'build.status_optional');
    });
    const on = new Set(this.state.bottles);
    const setMode = this.r && this.r.setMode;
    this.platesEl.querySelectorAll('[data-band]').forEach((b) => b.classList.toggle('is-on', on.has(b.dataset.band) || !!setMode));
    const rs = this.platesEl.querySelector('[data-rack-status]');
    if (rs) rs.textContent = setMode ? this.t('build.rack_all') : on.size ? this.t('build.rack_count', { count: on.size }) : this.t('actions.choose');
  }

  // ---------- your kit: everything you added stays visible under the machine, one chip each (tap a chip to take it out) ----------
  kitItems() {
    const r = this.r;
    const items = [];
    for (const l of r.lines) {
      if (l.kind === 'part') {
        const p = this.partOf(l.id);
        items.push({ key: `p${l.id}`, kind: 'part', v: String(l.id), img: p ? p.img : '', name: l.title, price: l.price });
      } else if (l.kind === 'bottle') {
        const b = this.cat.bottles.find((x) => x.id === l.id);
        items.push({ key: `b${b ? b.k : l.id}`, kind: 'bottle', v: b ? b.k : '', color: b ? b.c : '', name: l.title, price: l.price });
      }
    }
    // several parts from one brand ("Bubble Master 3-piece detail brush set", "Bubble Master tyre and wheel brush set") would both be cut to the brand: drop the words they share, so the chip says what the part is
    const words = (n) => String(n).split(/\s+/);
    items.filter((i) => i.kind === 'part').forEach((i, _x, parts) => {
      let cut = 0;
      parts.forEach((o) => {
        if (o === i) return;
        const a = words(i.name); const b = words(o.name); let n = 0;
        while (n < a.length - 1 && n < b.length && a[n].toLowerCase() === b[n].toLowerCase()) n++;
        cut = Math.max(cut, n);
      });
      if (cut) {
        const w = words(i.name);
        let from = cut;
        while (from > 0 && cut - from < 2 && w.slice(from).join(' ').length < 14) from--;   // a size or colour on its own says little: keep the last word or two of the shared name
        const rest = w.slice(from).join(' ');
        i.short = rest.charAt(0).toUpperCase() + rest.slice(1);
      }
    });
    if (r.setMode) items.push({ key: 'set', kind: 'set', v: 'all', color: '', name: this.t('build.all_three_set'), price: r.variant && this.machineData(this.state.machine).base ? r.variant.p - this.machineData(this.state.machine).base.p : 0 });
    return items;
  }

  kitRender() {
    if (!this.kitEl) return;
    const items = this.kitItems();
    const had = new Set([...this.kitEl.children].map((c) => c.dataset.key));
    const sig = items.map((i) => `${i.key}|${i.short || i.name}|${i.price}`).join('~');
    if (this.kitEl.dataset.sig === sig) return;
    this.kitEl.dataset.sig = sig;
    this.kitEl.dataset.n = String(items.length);
    this.kitEl.innerHTML = items.map((i) => `
      <button type="button" class="k4u-build__chipkit${had.size && !had.has(i.key) ? ' is-new' : ''}" data-act="kit" data-kind="${esc(i.kind)}" data-v="${esc(i.v)}" data-key="${esc(i.key)}" title="${esc(i.name)}" aria-label="${esc(this.t('build.kit_remove', { name: i.name }))}">
        <span class="k4u-build__chipkit-pic">${i.img ? `<img src="${esc(i.img)}" alt="" width="32" height="32" decoding="async">` : i.kind === 'set' ? this.cat.bottles.map((b) => `<i style="--c:${esc(b.c)}"></i>`).join('') : `<i style="--c:${esc(i.color)}"></i>`}</span>
        <span class="k4u-build__chipkit-txt"><b>${esc(i.short || i.name)}</b><small>${i.kind === 'part' ? '+' : ''}${this.core.money(i.price)}</small></span>
        <span class="k4u-build__chipkit-x" aria-hidden="true">&times;</span>
      </button>`).join('');
  }

  kitRemove(kind, v) {
    if (kind === 'part') this.choosePart(Number(v));
    else if (kind === 'bottle') this.toggleBottle(v);
    else if (kind === 'set') this.toggleAll();
  }

  // ---------- the steps ----------
  buildPanel() {
    this.panel.innerHTML = `
      <nav class="k4u-build__dots" data-dots aria-label="${esc(this.t('build.receipt'))}"></nav>
      <ol class="k4u-build__steps" data-steps></ol>
      <details class="k4u-build__box" data-box><summary>${esc(this.t('build.box_summary'))}</summary><ul data-box-list></ul></details>
      <details class="k4u-build__receipt" data-receipt open><summary data-receipt-sum>${esc(this.t('build.receipt'))}</summary><div data-receipt-body></div></details>
      <div class="k4u-build__bar">
        <div class="k4u-build__bar-top"><span class="k4u-build__total" aria-hidden="true" data-total>${this.core.money(this._r ? this._r.total : 0)}</span><span class="k4u-build__preorder" data-preorder>${esc(this.cat.preorder || '')}</span></div>
        <p class="k4u-build__err" role="alert" data-err hidden></p>
        <p class="k4u-build__why" data-why hidden></p>
        <div class="k4u-build__bar-act">
          <button type="button" class="k4u-btn" data-act="add" data-variant-id="">${esc(this.t('actions.add_to_cart'))}</button>
          <a class="k4u-btn k4u-btn--wa" data-wa target="_blank" rel="noopener" aria-label="${esc(this.t('build.wa_send'))}" href="${esc(this.cat.wa)}">${this.icons.wa || ''}WhatsApp</a>
        </div>
      </div>`;
    this.stepsEl = this.panel.querySelector('[data-steps]');
    this.dotsEl = this.panel.querySelector('[data-dots]');
    this.totalEl = this.panel.querySelector('[data-total]');
    this.addBtn = this.panel.querySelector('[data-act="add"]');
    this.waBtn = this.panel.querySelector('[data-wa]');
    this.errEl = this.panel.querySelector('[data-err]');
    this.whyEl = this.panel.querySelector('[data-why]');
    this.preEl = this.panel.querySelector('[data-preorder]');
    this.buildSteps();
  }

  stepList() {
    const out = this.dataset.fixed ? [] : [{ id: 'washer', title: this.t('build.step_washer') }]; // on a machine page the washer is already chosen
    for (const s of this.upgradableSlots()) out.push({ id: s.id, title: s.label, slot: s });
    if (this.cat.bottles.length) out.push({ id: 'liquids', title: this.t('build.step_liquids') });
    return out;
  }

  buildSteps() {
    const list = this.stepList();
    this.stepsEl.innerHTML = list.map((s) => `
      <li class="k4u-build__step" data-step="${esc(s.id)}" data-state="todo">
        <h3 class="k4u-build__sh" id="kb-h-${esc(s.id)}"><span class="k4u-build__sh-a">${esc(s.title)}.</span> <span class="k4u-build__sh-b">${esc(this.headSub(s))}</span></h3>
        <p class="k4u-build__sub" data-sub hidden></p>
        <div class="k4u-build__body" id="kb-b-${esc(s.id)}" role="group" aria-labelledby="kb-h-${esc(s.id)}"><div class="k4u-build__inner" data-inner></div></div>
      </li>`).join('');
    this.stepList_ = list;
    this.buildDots();
    this.boxRender();
    this.syncSteps();
  }

  // the second, grey half of a step's heading: "Foam pot. Keep it, or add a better one."
  headSub(s) {
    if (s.id === 'washer') return this.t('build.head_washer');
    if (s.id === 'liquids') return this.t('build.head_liquids');
    return this.t(s.slot && s.slot.stock[0] && this.slotMeta(s.id).mode !== 'many' ? 'build.head_keep' : 'build.head_add');
  }

  // a column of dots beside the steps (desktop): where you are, and a jump to any step
  buildDots() {
    if (!this.dotsEl) return;
    this.dotsEl.innerHTML = `<ol>${this.stepList_.map((s) => `<li><button type="button" data-act="dot" data-v="${esc(s.id)}" aria-label="${esc(s.title)}" tabindex="-1"></button></li>`).join('')}</ol>`;
    this._dotIO?.disconnect();
    if (!('IntersectionObserver' in window)) return;
    const seen = new Map();
    this._dotIO = new IntersectionObserver((es) => {
      es.forEach((e) => seen.set(e.target.dataset.step, e.isIntersecting ? e.intersectionRatio : 0));
      let best = null;
      seen.forEach((r, id) => { if (r > 0 && (!best || r > best[1])) best = [id, r]; });
      const btns = [...this.dotsEl.querySelectorAll('button')];
      btns.forEach((b) => b.setAttribute('aria-current', String(!!best && b.dataset.v === best[0])));
      const at = best ? btns.findIndex((b) => b.dataset.v === best[0]) : 0;
      this.dotsEl.style.setProperty('--k4u-fill', `${4 + Math.max(0, at) * 18}px`); // the dark capsule grows to the step you are on
    }, { rootMargin: '-25% 0px -45% 0px', threshold: [0, .25, .5, .75, 1] });
    this.stepsEl.querySelectorAll('.k4u-build__step').forEach((li) => this._dotIO.observe(li));
  }

  boxRender() {
    const k = this.state.machine;
    const all = this.stockFor(k);
    const upgradable = new Set(this.upgradableSlots().map((s) => s.id));
    const rest = all.filter((b) => !upgradable.has(b.slot));
    this.panel.querySelector('[data-box-list]').innerHTML = rest.map((b) => `<li><span class="k4u-build__box-pic"><img src="${esc(b.img)}" alt="" width="56" height="56" loading="lazy" decoding="async"></span><span><b>${esc(b.t)}</b>${b.s ? `<small>${esc(b.s)}</small>` : ''}</span></li>`).join('');
    this.panel.querySelector('[data-box]').hidden = !rest.length;
  }

  stepStatus(s) {
    const m = this.machineData(this.state.machine);
    const r = this.r;
    if (s.id === 'washer') return { sub: `Lutian ${m.n}, ${this.core.money(m.base.p)}`, cta: this.t('build.cta_change'), pic: m.img, done: true };
    if (s.id === 'liquids') {
      const n = this.state.bottles.length;
      return { sub: this.t(r && r.setMode ? 'build.all_three_set' : n ? 'build.bottles_of_three' : 'build.none_chosen', { count: n }), cta: this.t(n ? 'build.cta_change' : 'actions.choose'), pic: '', done: n > 0, bands: true };
    }
    const eq = this.equippedIn(s.id);
    const stock = s.slot.stock[0];
    if (eq) {
      let name = eq.part.t;
      if (this.slotMeta(s.id).mode === 'many') { // several items can be on: say which ones, and which size of each
        const groups = new Map();
        for (const id of this.state.parts) {
          const p = this.partOf(id);
          if (!p || p.slot !== s.id) continue;
          const v = p.v.find((x) => x.id === id);
          if (!groups.has(p)) groups.set(p, []);
          if (p.v.length > 1 && v && v.t) groups.get(p).push(v.t);
        }
        name = [...groups].map(([p, vs]) => (vs.length ? `${p.t} ${vs.join(' + ')}` : p.t)).join(', ');
      }
      return { sub: this.t('build.sub_upgraded', { name }), cta: this.t('build.cta_change'), pic: eq.part.img, done: true, up: true };
    }
    if (stock) return { sub: this.t('build.sub_in_box', { name: stock.t }), cta: this.t('build.cta_upgrade'), pic: stock.img, done: false };
    return { sub: this.t('build.status_optional'), cta: this.t('build.cta_add'), pic: '', done: false };
  }

  syncSteps() {
    if (!this.stepsEl) return;
    this.stepList_.forEach((s) => {
      const li = this.stepsEl.querySelector(`[data-step="${s.id}"]`);
      if (!li) return;
      const st = this.stepStatus(s);
      li.dataset.state = st.up ? 'up' : st.done ? 'done' : 'todo';
      const sub = li.querySelector('[data-sub]');
      sub.hidden = !st.up;
      if (st.up) sub.textContent = st.sub;
      this.fillBody(s.id, true);
    });
  }

  openStep(id, { scroll = true, silent = false } = {}) {
    this.open = id;
    if (scroll) { clearTimeout(this._scrollT); this._scrollT = setTimeout(() => this.scrollToStep(id), 0); }
    if (!silent) this.closeHintOnce();
  }

  scrollToStep(id) {
    const li = this.stepsEl.querySelector(`[data-step="${id}"]`);
    if (!li) return;
    const y = li.getBoundingClientRect().top;
    const phone = innerWidth < 990;
    const top = phone ? (document.querySelector('header')?.getBoundingClientRect().height || 60) + this.stage.getBoundingClientRect().height + 16 : (document.querySelector('header')?.getBoundingClientRect().height || 66) + 40;
    if (Math.abs(y - top) < 6) return;
    const wrap = document.querySelector('.page-wrapper');
    const scroller = !phone && wrap && getComputedStyle(wrap).overflowY !== 'visible' ? wrap : window;
    scroller.scrollBy({ top: y - top, behavior: calm() ? 'auto' : 'smooth' });
  }

  closeHintOnce() { if (this.hintEl) this.hintEl.classList.add('is-gone'); }

  /** Fill (or just refresh the ticks of) an open step's body. */
  fillBody(id, refreshOnly) {
    const li = this.stepsEl.querySelector(`[data-step="${id}"]`);
    if (!li) return;
    const inner = li.querySelector('[data-inner]');
    const key = `${id}|${this.state.machine}|${this.state.bottles.length === 3}`;
    if (refreshOnly && inner.dataset.key === key) { this.tickBody(inner, id); return; }
    inner.dataset.key = key;
    const s = this.stepList_.find((x) => x.id === id);
    if (id === 'washer') inner.innerHTML = this.bodyWasher();
    else if (id === 'liquids') inner.innerHTML = this.bodyLiquids();
    else if (s && s.slot) inner.innerHTML = this.bodySlot(s.slot);
    this.tickBody(inner, id);
  }

  check() { return `<span class="k4u-build__tick" aria-hidden="true">${this.icons.check || ''}<em>${esc(this.t('build.added'))}</em></span>`; }
  pic(src, alt = '') { return src ? `<img src="${esc(src)}" alt="${esc(alt)}" width="72" height="72" loading="lazy" decoding="async">` : ''; }

  bodyWasher() {
    const money = this.core.money;
    const a = this.cat.machines;
    const maxBar = Math.max(...a.map((m) => m.spec.barN || 0)) || 1;
    const maxW = Math.max(...a.map((m) => m.spec.wN || 0)) || 1;
    return `<div class="k4u-build__cmp" role="radiogroup" aria-label="${esc(this.t('build.washer_group'))}">${a.map((m, i) => `
      <button type="button" class="k4u-build__card" role="radio" aria-checked="false" data-act="machine" data-v="${esc(m.k)}" data-press>
        <b class="k4u-build__card-n">${esc(m.n)}</b><span class="k4u-build__card-p">${money(m.base.p)}</span>
        <span class="k4u-build__spec"><small>${esc(this.t('build.spec_pressure'))}</small>${esc(m.spec.bar)}${m.spec.barN ? `<span class="k4u-build__meter"><i style="--v:${(m.spec.barN / maxBar).toFixed(3)};--i:${i}"></i></span>` : ''}</span>
        <span class="k4u-build__spec"><small>${esc(this.t('build.spec_motor'))}</small>${esc(m.spec.w)}${m.spec.wN ? `<span class="k4u-build__meter"><i style="--v:${(m.spec.wN / maxW).toFixed(3)};--i:${i}"></i></span>` : ''}</span>
        <span class="k4u-build__spec"><small>${esc(this.t('build.spec_hose'))}</small>${esc(m.spec.hose)}</span>
        ${m.adds ? `<span class="k4u-build__spec"><small>${esc(this.t('build.spec_different'))}</small>${esc(m.adds)}</span>` : ''}${this.check()}</button>`).join('')}</div>
      <a class="k4u-build__help" href="${esc(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'pages/f4-vs-p6-pro')}"><span><b>${esc(this.t('build.compare_title'))}</b><small>${esc(this.t('build.compare_sub'))}</small></span><i aria-hidden="true">&rsaquo;</i></a>`;
  }

  bodyLiquids() {
    const r = this.r;
    const money = this.core.money;
    const hasSet = r.variant && this.machineData(this.state.machine).set;
    const all = hasSet ? `
      <button type="button" class="k4u-build__choice k4u-build__choice--all" role="checkbox" aria-checked="false" data-act="all" data-press>
        <span class="k4u-build__choice-txt"><b>${esc(this.t('build.all_three_set'))}</b><span data-all-note></span><small class="k4u-build__save" data-save hidden></small></span>${this.check()}</button>` : '';
    const bottles = this.cat.bottles.map((b) => `
      <button type="button" class="k4u-build__bottle" style="--c:${esc(b.c)}" data-k="${esc(b.k)}" role="checkbox" aria-checked="false" data-act="bottle" data-v="${esc(b.k)}" data-press${b.a ? '' : ' aria-disabled="true"'}>
        <span class="k4u-build__num"><span class="k4u-build__num-line" aria-hidden="true">${esc(b.num || b.k + '.0')}</span><span class="k4u-build__num-fill">${esc(b.num || b.k + '.0')}</span></span>
        <b>${esc(b.n)}</b><span>500 ml, ${money(b.p)}</span><small data-third="${esc(b.k)}"></small>${this.check()}${b.a ? '' : `<span class="k4u-slash" aria-hidden="true">${esc(this.t('content.product_badge_sold_out'))}</span>`}</button>`).join('');
    return `<p class="k4u-build__ask">${esc(this.t('build.liquids_ask'))}</p>${all}<div class="k4u-build__bottles">${bottles}</div>`;
  }

  bodySlot(slot) {
    const money = this.core.money;
    const k = this.state.machine;
    const stock = slot.stock[0];
    const meta = this.slotMeta(slot.id);
    const lc = (x) => (/^en\b/i.test(this.cat.lang || 'en') ? x.toLowerCase() : x); // only the English sentences want "a foam cannon" in lower case
    const ask = meta.ask || (stock ? this.t('build.ask_upgrade', { machine: this.machineData(k).n, part: lc(stock.t) }) : this.t('build.ask_add', { part: lc(slot.label) }));
    const keep = stock ? `
      <button type="button" class="k4u-build__choice" role="radio" aria-checked="false" data-act="stock" data-v="${esc(slot.id)}" data-press>
        <span class="k4u-build__choice-pic">${this.pic(stock.img, stock.t)}</span>
        <span class="k4u-build__choice-txt"><b>${esc(this.t('build.keep_stock'))}</b><span>${esc(stock.t)}</span></span>
        <span class="k4u-build__choice-price">${esc(this.t('build.included'))}</span>${this.check()}</button>` : `
      <button type="button" class="k4u-build__choice k4u-build__choice--plain" role="radio" aria-checked="false" data-act="stock" data-v="${esc(slot.id)}" data-press>
        <span class="k4u-build__choice-txt"><b>${esc(this.t('build.no_thanks'))}</b><span>${esc(this.t('build.skip_this'))}</span></span><span class="k4u-build__choice-price">RM0</span>${this.check()}</button>`;
    const ups = slot.options.map((o) => {
      const part = this.partOf(o.variantId) || {};
      const fit = o.fit === 'unknown' ? this.t('build.fit_unknown') : this.t('build.fit_yes', { machine: this.machineData(k).n });
      const order = part.ph ? (waOnlyMode() ? `<small class="k4u-build__order k4u-build__order--plain">${esc(this.t('build.confirm_wa'))}</small>` : `<small class="k4u-build__order">${esc(this.t('build.order_wa'))}</small>`) : '';
      return `
      <button type="button" class="k4u-build__choice" role="radio" aria-checked="false" data-act="part" data-v="${o.variantId}" data-press${o.available ? '' : ' aria-disabled="true"'}>
        <span class="k4u-build__choice-pic">${this.pic(o.img, o.title)}</span>
        <span class="k4u-build__choice-txt"><b>${esc(o.title)}</b>${o.sentence ? `<span>${esc(o.sentence)}</span>` : ''}<small>${esc(fit)}</small>${order}</span>
        <span class="k4u-build__choice-price">${o.available ? `+${money(o.price)}` : esc(this.t('content.product_badge_sold_out'))}</span>${this.check()}</button>`;
    }).join('');
    const note = stock && slot.mode === 'one' ? `<p class="k4u-build__fine k4u-build__keep" data-keep hidden>${esc(meta.keep || this.t('build.keep_default'))}</p>` : '';
    return `<p class="k4u-build__ask">${esc(ask)}</p><div class="k4u-build__choices" role="radiogroup" aria-label="${esc(slot.label)}">${keep}${ups}</div>${note}`;
  }

  tickBody(inner, id) {
    const S = this.state;
    const r = this.r;
    if (id === 'washer') inner.querySelectorAll('[data-act="machine"]').forEach((el) => el.setAttribute('aria-checked', String(el.dataset.v === S.machine)));
    else if (id === 'liquids') {
      inner.querySelectorAll('[data-act="bottle"]').forEach((el) => el.setAttribute('aria-checked', String(S.bottles.includes(el.dataset.v) || r.setMode)));
      const allEl = inner.querySelector('[data-act="all"]');
      if (allEl) {
        allEl.setAttribute('aria-checked', String(r.setMode));
        allEl.querySelector('[data-all-note]').textContent = r.setMode ? this.t('build.comes_with') : (r.nudge.all ? r.nudge.all.text : '');
        const save = allEl.querySelector('[data-save]');
        const v = r.saving && r.saving.vsSet;
        save.hidden = !v;
        if (v) save.textContent = this.t('build.save_vs_set', { amount: this.core.money(v) });
      }
      inner.querySelectorAll('[data-third]').forEach((el) => {
        const t = r.nudge.third;
        el.textContent = t && t.bottle === el.dataset.third && !S.bottles.includes(el.dataset.third) ? this.t('build.add_it', { delta: t.text }) : '';
      });
    } else {
      const eq = this.equippedIn(id);
      inner.querySelectorAll('[data-act="part"]').forEach((el) => el.setAttribute('aria-checked', String(S.parts.includes(Number(el.dataset.v)))));
      inner.querySelectorAll('[data-act="stock"]').forEach((el) => el.setAttribute('aria-checked', String(!eq)));
      const keep = inner.querySelector('[data-keep]');
      if (keep) keep.hidden = !eq;
    }
  }

  // ---------- render after any change ----------
  render(info = {}) {
    const r = this.core.resolve(this.state, this.cat);
    this._r = r;
    const k = this.state.machine;
    this.seg.querySelectorAll('button').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.v === k)));
    this.syncSteps();
    this.plateState();
    this.kitRender();
    this.waBtn.href = `${this.cat.wa}?text=${encodeURIComponent(this.core.waText(r, this.cat, `${this.linkBase()}#${this.core.encode(this.state)}`))}`;
    this.leadData = JSON.stringify({ content_type: 'product', content_name: `Washer build ${r.code}`, value: r.total / 100, currency: 'MYR', num_items: r.lines.length });
    this.waBtn.dataset.k4uLead = this.leadData;
    this.renderReceipt();
    const ph = r.lines.some((l) => l.kind === 'part' && isPlaceholder(l.id));
    const waOnly = waOnlyMode();   // no online checkout: the whole build is ordered on WhatsApp
    this.addBtn.hidden = waOnly;
    this.addBtn.dataset.variantId = r.variant ? r.variant.id : '';
    this.addBtn.disabled = waOnly || !r.variant || !r.variant.a || ph;
    this.waBtn.classList.toggle('is-lead', waOnly || ph);
    this.whyEl.hidden = waOnly || !ph;
    if (ph && !waOnly) this.whyEl.textContent = this.t('build.note_wa_only');
    this.preEl.textContent = ph ? '' : (this.cat.preorder || '');
    if (waOnly) { const txt = [...this.waBtn.childNodes].reverse().find((n) => n.nodeType === 3 && n.textContent.trim()); if (txt) txt.textContent = this.t('build.wa_send'); }
    const text = this.core.money(r.total);
    if (this.fx && !calm() && this._lastTotal != null && this._lastTotal !== r.total) this.fx.roll(this.totalEl, text); else this.totalEl.textContent = text;
    this._lastTotal = r.total;
    this.announce(this.t('build.announce', { summary: r.summary, total: text }));
    this.writeUrl();
    if (!this.dataset.fixed) mem.set('k4u-build:last', this.core.encode(this.state));
    if (info.equipped && this.fx && !calm()) this.flight(info);
  }

  flight(info) {
    const tile = this.stepsEl.querySelector(`[data-act="part"][data-v="${info.equipped}"] img`);
    const plate = this.platesEl.querySelector(`.k4u-build__plate[data-slot="${info.slot}"]`);
    if (tile && plate) this.fx.flight(this, tile, plate);
  }

  renderReceipt() {
    const r = this.r;
    const money = this.core.money;
    const ph = r.lines.some((l) => l.kind === 'part' && isPlaceholder(l.id));
    this.panel.querySelector('[data-receipt-sum]').textContent = this.pl('build.receipt_lines', r.lines.length);
    const lines = r.lines.map((l) => `<li><span>${esc(l.title)}${l.fitUnknown ? ` <small>${esc(this.t('build.fit_to_confirm'))}</small>` : ''}</span><b>${money(l.price)}</b></li>`).join('');
    const setNote = r.setMode ? `<p class="k4u-build__fine">${esc(this.t('build.note_set'))}</p>` : '';
    const vs = r.setMode && r.saving && r.saving.vsSingles ? `<p class="k4u-build__fine">${esc(this.t('build.note_vs_singles', { amount: money(r.saving.vsSingles) }))}</p>` : '';
    const spare = r.spares.length ? `<p class="k4u-build__fine">${esc(this.t('build.note_spare'))}</p>` : '';
    const order = '';   // the bar says why the cart button is off, right next to the buttons
    const share = navigator.share ? `<button type="button" data-act="share">${esc(this.t('build.share'))}</button>` : '';
    const cartNote = this.inCart && !waOnlyMode() ? `<p class="k4u-build__fine">${esc(this.t('build.note_in_cart'))}</p>` : '';
    this.panel.querySelector('[data-receipt-body]').innerHTML = `<ul class="k4u-build__lines">${lines}</ul>${setNote}${vs}${spare}${order}${cartNote}
      <p class="k4u-build__fine">${esc(this.t(waOnlyMode() ? 'build.note_delivery_wa' : 'build.note_delivery'))}</p>
      <div class="k4u-build__links"><a href="${esc(this.waBtn.href)}" target="_blank" rel="noopener" data-k4u-lead="${esc(this.leadData || '')}">${esc(this.t('build.wa_send'))}</a><button type="button" data-act="copy">${esc(this.t('build.copy_link'))}</button>${share}</div>`;
  }

  say(text) {
    this.chip.textContent = text;
    this.chip.hidden = false;
    this.liveEl.textContent = text;
    clearTimeout(this._chipT);
    this._chipT = setTimeout(() => { this.chip.hidden = true; }, 2600);
  }

  pushUrl() {
    if (this.dataset.fixed) return;
    const enc = this.core.encode(this.state);
    try { history.replaceState(null, '', enc ? `#${enc}` : location.pathname + location.search); } catch { /* sandboxed */ }
  }

  // ---------- events ----------
  async onClick(e) {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const open = t.closest('[data-open]');
    if (open && open.hasAttribute('href') && !(e.metaKey || e.ctrlKey || e.shiftKey)) {
      e.preventDefault();
      this.pickMachine(open.dataset.open, this.floor.querySelector(`[data-tile="${open.dataset.open}"] img`)?.getBoundingClientRect());
      return;
    }
    const tile = t.closest('[data-tile]');
    if (tile) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || !this.core) return;
      e.preventDefault();
      const img = tile.querySelector('img');
      this.pickMachine(tile.dataset.tile, img ? img.getBoundingClientRect() : null);
      return;
    }
    const el = t.closest('[data-act]');
    if (!el || el.getAttribute('aria-disabled') === 'true') return;
    const v = el.dataset.v;
    switch (el.dataset.act) {
      case 'machine': if (v !== this.state.machine) this.pickMachine(v); break;
      case 'dot': this.openStep(v, { silent: true }); break;
      case 'kit': this.kitRemove(el.dataset.kind, v); break;
      case 'plate': this.openStep(el.dataset.slot); break;
      case 'bottle': this.toggleBottle(v); break;
      case 'all': this.toggleAll(); break;
      case 'part': this.choosePart(Number(v), el.querySelector('img')); break;
      case 'stock': this.keepStock(v); break;
      case 'add': this.addToCart(el); break;
      case 'copy': this.copyLink(el); break;
      case 'share': navigator.share({ title: this.t('build.share_title'), url: `${this.linkBase()}#${this.core.encode(this.state)}` }).catch(() => {}); break;
      default:
    }
  }

  async copyLink(btn) {
    const url = `${this.linkBase()}#${this.core.encode(this.state)}`;
    const label = btn.textContent;
    try { await navigator.clipboard.writeText(url); btn.textContent = this.t('build.copied'); } catch { btn.textContent = this.t('build.copy_fail'); }
    setTimeout(() => { btn.textContent = label; }, 1200);
  }

  async cartCheck() {
    try {
      const cart = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then((x) => x.json());
      const ids = new Set(this.cat.machines.flatMap((m) => [m.base && m.base.id, m.set && m.set.id]).filter(Boolean));
      this.inCart = cart.items.some((i) => ids.has(i.variant_id));
      if (this.inCart && this._r) this.renderReceipt();
    } catch { /* offline */ }
  }

  async addToCart(button) {
    const r = this.r;
    if (!r || !r.variant || button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.errEl.hidden = true;
    const sections = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean).join(',');
    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'cart',
      lines: r.items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    }));
    const finish = async (ok, added) => {
      const cart = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then((x) => x.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: ok && added ? added.sections : undefined, source: 'k4u-build', sourceId: 'k4u-build', itemCount: r.items.length, didError: !ok },
      });
    };
    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: r.items, sections }),
      });
      const added = await res.json();
      const ok = res.ok && !added.status;
      await finish(ok, added);
      if (ok) button.dispatchEvent(new CustomEvent('k4u:added', { bubbles: true }));
      else throw new Error(added.description || 'add failed');
    } catch {
      try { await finish(false); } catch { deferred.reject(new Error('add failed')); }
      this.errEl.textContent = this.t('build.add_error');
      this.errEl.hidden = false;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('k4u-build')) customElements.define('k4u-build', K4UBuild);
